import React from 'react';
import DatePicker from './DatePicker';
import { useI18n } from '../../i18n/I18nContext';
import { todayYmd } from '../../utils/datetime';

/**
 * DateRangePicker — two DatePickers with coupled min / max plus preset chips
 * (Bugun, 7 kun, 30 kun, 90 kun), spec §3.10. Values are "YYYY-MM-DD" or "".
 *
 *   <DateRangePicker from={start} to={end} onChange={({ from, to }) => { setStart(from); setEnd(to); }} />
 *
 * `presets` are day counts ending today: 0 = today only, 7 = the last 7 days including today.
 * Pass `presets={[]}` for no chips. `min` / `max` bound both ends.
 */

const pad2 = (n) => String(n).padStart(2, '0');

/** The day `days` before `ymd`, as "YYYY-MM-DD" (local calendar, no time-zone drift). */
export function shiftYmd(ymd, days) {
  const [y, m, d] = ymd.split('-').map(Number);
  const next = new Date(y, m - 1, d + days, 12);
  return `${next.getFullYear()}-${pad2(next.getMonth() + 1)}-${pad2(next.getDate())}`;
}

function DateRangePicker({
  from = '',
  to = '',
  onChange,
  presets = [0, 7, 30, 90],
  min,
  max,
  className = '',
  fromLabel,
  toLabel,
}) {
  const { t } = useI18n();
  const today = todayYmd();

  const rangeOf = (count) => (count <= 0 ? { from: today, to: today } : { from: shiftYmd(today, -(count - 1)), to: today });
  const isActive = (count) => {
    if (!today || !from || !to) return false;
    const r = rangeOf(count);
    return r.from === from && r.to === to;
  };
  const presetLabel = (count) => (count <= 0 ? t('ui.rangeToday') : t('ui.rangeLastDays', { count }));

  return (
    <div className={`ui-range${className ? ` ${className}` : ''}`}>
      <div className="ui-range__pickers">
        <DatePicker
          value={from}
          onChange={(next) => onChange?.({ from: next, to })}
          min={min}
          max={to || max}
          aria-label={fromLabel || t('ui.rangeFromAria')}
        />
        <DatePicker
          value={to}
          onChange={(next) => onChange?.({ from, to: next })}
          min={from || min}
          max={max}
          aria-label={toLabel || t('ui.rangeToAria')}
        />
      </div>
      {presets.length > 0 && today && (
        <div className="ui-chip-row" role="group">
          {presets.map((count) => {
            const r = rangeOf(count);
            const outside = Boolean((min && r.from < min) || (max && r.to > max));
            return (
              <button
                key={count}
                type="button"
                className="ui-chip"
                aria-pressed={isActive(count)}
                disabled={outside}
                onClick={() => onChange?.(r)}
              >
                {presetLabel(count)}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
